import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import BarChart from '@mui/icons-material/BarChart';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { ParamsContext } from 'providers/ParamsContext';
import { FakeText, TextField, Button, IconButton } from '@totalsoft/rocket-ui';
import { get } from '@totalsoft/change-tracking-react';
import DetailedSlider from 'components/common/inputs/DetailedSlider';
import { RightsContext } from 'providers/RightsContext';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import excelIcon from 'assets/img/microsoft-excel-icon.svg';
import pdfIcon from 'assets/img/pdf-icon.svg';
import wordIcon from 'assets/img/microsoft-word-icon.svg';

const VatFinancingCard = ({
  vatFinancingLens,
  onPropertyChange,
  cardExpanded,
  onExpand,
  loading,
  onSave,
  isDirty,
  saving,
  validation,
  exportPaymentPlan,
  exportPaymentPlanWithTemplateOutput
}) => {
  const { t } = useTranslation();
  const { cvsChAmount, cvsChPercent } = useContext(ParamsContext);
  const { readOnlyFull } = useContext(RightsContext);

  return (
    <CollapseOrBlankCard
      id='vatFinancingCard'
      icon={BarChart}
      title={t('OfferEdit.VATFinancing.Title')}
      expanded={cardExpanded}
      onToggle={onExpand}
      canExpand={true}
    >
      {loading ? (
        <FakeText lines={2} />
      ) : (
        <Grid container spacing={4} px={1}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id={'vatFinancingCard_amount'}
              fullWidth
              isNumeric
              value={vatFinancingLens.amount |> get}
              label={t('OfferEdit.VATFinancing.Amount')}
              disabled={true}
              inputProps={{
                decimalScale: cvsChAmount
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField
              id={'vatFinancingCard_interest'}
              fullWidth
              isNumeric
              value={vatFinancingLens.interestPercent |> get}
              label={t('OfferEdit.VATFinancing.InterestPercent')}
              disabled={true}
              inputProps={{
                decimalScale: cvsChPercent
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DetailedSlider
              id={'vatFinancingCard_durationMonths'}
              value={vatFinancingLens.durationMonths |> get}
              defaultValue={vatFinancingLens.durationMonths |> get}
              min={1}
              max={vatFinancingLens.maxDurationMonths |> get}
              step={1}
              label={t('OfferEdit.VATFinancing.DurationMonths')}
              onChange={onPropertyChange('durationMonths')}
              fullWidth
              decimalScale={0}
              error={false}
              helperText={''}
              disabled={readOnlyFull || saving}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DetailedSlider
              id={'vatFinancingCard_grace'}
              value={vatFinancingLens.graceMonths |> get}
              defaultValue={vatFinancingLens.graceMonths |> get}
              min={0}
              max={(vatFinancingLens.durationMonths |> get) - 1}
              step={1}
              label={t('OfferEdit.VATFinancing.GraceMonths')}
              onChange={onPropertyChange('graceMonths')}
              fullWidth
              decimalScale={0}
              error={!isValid(validation.graceMonths)}
              helperText={getErrors(validation.graceMonths)}
              disabled={readOnlyFull || saving}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <b>{t('OfferEdit.PaymentPlan.Title')}:</b>
            <label htmlFor='exportFile'>
              <IconButton
                id={'offerPaymentPlanCard_exportBtn'}
                onClick={() => exportPaymentPlan('excel')}
                tooltip={t('OfferEdit.FinancialServices.Export', {
                  data: { exportType: 'excel' }
                })}
                variant='text'
              >
                <img src={excelIcon} />
              </IconButton>
            </label>
            {exportPaymentPlanWithTemplateOutput && (
              <label htmlFor='exportFile'>
                <IconButton
                  id={'offerPaymentPlanCard_exportBtn'}
                  disabled={readOnlyFull}
                  onClick={() => exportPaymentPlan(exportPaymentPlanWithTemplateOutput)}
                  tooltip={t('OfferEdit.FinancialServices.Export', {
                    data: { exportType: exportPaymentPlanWithTemplateOutput }
                  })}
                  variant='text'
                >
                  <img src={exportPaymentPlanWithTemplateOutput === 'pdf' ? pdfIcon : wordIcon} />
                </IconButton>
              </label>
            )}
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Button
              id={'vatFinancingCard_saveBtn'}
              color='primary'
              variant='contained'
              onClick={onSave}
              disabled={readOnlyFull || !isDirty || saving}
            >
              {saving ? t('Saving') + '...' : t('Save')}
            </Button>
          </Grid>
        </Grid>
      )}
    </CollapseOrBlankCard>
  );
};

VatFinancingCard.propTypes = {
  vatFinancingLens: PropTypes.object,
  onExpand: PropTypes.func.isRequired,
  cardExpanded: PropTypes.bool,
  loading: PropTypes.bool,
  onPropertyChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isDirty: PropTypes.bool,
  saving: PropTypes.bool,
  validation: PropTypes.object,
  exportPaymentPlan: PropTypes.func.isRequired,
  exportPaymentPlanWithTemplateOutput: PropTypes.string
};

export default VatFinancingCard;
